import { cloneDeep } from 'lodash';
import { ActorInfo } from 'sr-types/lib/note/v1/graphql';
import type { UserProfile } from '../../auth/api';
import { AllPermissions } from '../../auth/permissions';
import { transformNameToDisplay } from '../../utils/commonUtils';
import type { ShareAccess, ShareAccessDisplayItem, ShareAction, ShareSettingsViewModel } from '../types';

/**
 * Creates a `ShareAccess` entry for the owner of the entity record.
 * Extract from the initial share settings if available, otherwise constructs it from entityCreatedBy.
 */
export function makeShareAccessEntryForEntityRecordOwner(
    shareSettingsProp: ShareSettingsViewModel
): ShareAccess | undefined {
    let ownerEntry: ShareAccess | undefined = undefined;

    if (shareSettingsProp.entityCreatedBy) {
        ownerEntry = {
            action: AllPermissions.Manage,
            actor: {
                id: shareSettingsProp.entityCreatedBy,
                name: shareSettingsProp.entityCreatedByName ?? shareSettingsProp.entityCreatedBy,
                email: ''
            },
            isOwner: true
        };
    }

    if (shareSettingsProp.accessLevel === 'restricted') {
        const existingOwnerEntry = shareSettingsProp.sharedWith.find((s) => s.isOwner);

        if (existingOwnerEntry) {
            return cloneDeep(existingOwnerEntry);
        }

        return ownerEntry;
    }

    return ownerEntry;
}

/**
 * Creates a `ShareAccess` entry for the logged-in user.
 * Extract from the initial share settings if available, otherwise constructs it from the user profile.
 */
export function makeShareAccessEntryForLoggedInUser(
    shareSettingsProp: ShareSettingsViewModel,
    userProfile: UserProfile | undefined
): ShareAccess | undefined {
    let loggedInUserEntry: ShareAccess | undefined = undefined;

    if (userProfile) {
        loggedInUserEntry = {
            action: AllPermissions.Manage,
            actor: {
                id: userProfile.accountId,
                name: transformNameToDisplay(userProfile.name),
                email: userProfile.username
            }
        };
    }

    if (shareSettingsProp.accessLevel === 'restricted') {
        const existingLoggedInUserEntry = shareSettingsProp.sharedWith.find(
            (s) => s.actor.id === userProfile?.accountId
        );

        if (existingLoggedInUserEntry) {
            return cloneDeep(existingLoggedInUserEntry);
        }

        return loggedInUserEntry;
    }

    return loggedInUserEntry;
}

/**
 * Creates the initial state for the share settings form, ensuring that if the access level is 'restricted',
 * there is an entry for the entity record owner in the sharedWith list.
 * This is important because the form relies on the presence of the owner's entry to function correctly,
 * and the initial share settings may not include it if the owner wasn't explicitly listed
 * as a sharedWith entry - say for when the entity is newly created.
 */
export function makeInitialShareSettingsState(
    shareSettingsProp: ShareSettingsViewModel,
    shareAccessEntryForEntityRecordOwner: ShareAccess | undefined
): ShareSettingsViewModel {
    const initialShareSettingsState: ShareSettingsViewModel = cloneDeep(shareSettingsProp);

    if (
        shareSettingsProp.accessLevel === 'restricted' &&
        !initialShareSettingsState.sharedWith.some((s) => s.isOwner) &&
        shareAccessEntryForEntityRecordOwner
    ) {
        initialShareSettingsState.sharedWith.push(shareAccessEntryForEntityRecordOwner);
    }

    return initialShareSettingsState;
}

/**
 * Performs a deep equality check on two `ShareSettingsViewModel` objects, ignoring order of sharedWith entries.
 * This is used to determine if there are unsaved changes in the share settings form.
 */
export function isEqualShareSettingsViewModel(
    settingsA: ShareSettingsViewModel,
    settingsB: ShareSettingsViewModel
): boolean {
    if (settingsA.accessLevel !== settingsB.accessLevel) return false;
    if (settingsB.accessLevel === 'open') {
        return isEqualActorList(settingsA.notifyContacts ?? [], settingsB.notifyContacts ?? []);
    }

    if (!isEqualShareAccessList(settingsA.sharedWith, settingsB.sharedWith)) return false;
    if (!isEqualShareAccessList(settingsA.sharedWithDepartments, settingsB.sharedWithDepartments)) return false;
    return true;
}

function isEqualShareAccessList(listA: ShareAccess[], listB: ShareAccess[]): boolean {
    if (listA.length !== listB.length) return false;

    const sortedListA = [...listA].sort((a, b) => a.actor.id.localeCompare(b.actor.id));
    const sortedListB = [...listB].sort((a, b) => a.actor.id.localeCompare(b.actor.id));

    for (let i = 0; i < sortedListA.length; i++) {
        const entryA = sortedListA[i];
        const entryB = sortedListB[i];
        if (
            entryA.actor.id !== entryB.actor.id ||
            entryA.action !== entryB.action ||
            entryA.isOwner !== entryB.isOwner
        ) {
            return false;
        }
    }

    return true;
}

/**
 * Merges a list of actors to add into the existing sharedWith list, ensuring no duplicates and assigning a default permission.
 * This is used when adding new actors to share with, to create the updated sharedWith list for the share settings payload.
 */
export function mergeActorsToAddIntoSharedWith(
    sharedWith: ShareAccess[],
    actorsToAdd: Array<ActorInfo>,
    defaultPermission: ShareAction
): ShareAccess[] {
    const existingIds = new Set(sharedWith.map((s) => s.actor.id));
    const newActorsToShareWith: ShareAccess[] = actorsToAdd
        .filter((a) => a.id && !existingIds.has(a.id))
        .map((a) => ({
            action: defaultPermission,
            actor: { id: a.id, name: String(a.name ?? ''), email: a.email ?? '' }
        }));
    return [...sharedWith, ...newActorsToShareWith];
}

export function mergeDepartmentsToAddIntoSharedWithDepartments(
    sharedWithDepartments: ShareAccess[],
    departmentsToAdd: string[],
    defaultPermission: ShareAction
): ShareAccess[] {
    const existingIds = new Set(sharedWithDepartments.map((s) => s.actor.id));
    const newDepartmentsToShareWith: ShareAccess[] = departmentsToAdd
        .map((department) => department?.trim())
        .filter((department): department is string => Boolean(department && !existingIds.has(department)))
        .map((department) => ({
            action: defaultPermission,
            actor: { id: department, name: department, email: '' }
        }));

    return [...sharedWithDepartments, ...newDepartmentsToShareWith];
}

export function makeSharedWithDisplayList(
    sharedWith: ShareAccess[] = [],
    initialSharedWith: ShareAccess[] = [],
    sharedWithDepartments: ShareAccess[] = [],
    initialSharedWithDepartments: ShareAccess[] = []
): ShareAccessDisplayItem[] {
    const accountDisplayList = sharedWith.map((item) => {
        const initialItem = initialSharedWith.find((s) => s.actor.id === item.actor.id);
        const hasChanged = initialItem ? item.action !== initialItem.action : true;
        return { ...item, hasChanged, source: 'accounts' as const };
    });

    const departmentDisplayList = sharedWithDepartments.map((item) => {
        const initialItem = initialSharedWithDepartments.find((s) => s.actor.id === item.actor.id);
        const hasChanged = initialItem ? item.action !== initialItem.action : true;
        return { ...item, hasChanged, source: 'departments' as const };
    });

    const sharedWithDisplayList = [...accountDisplayList, ...departmentDisplayList].sort((a, b) => {
        if (a.isOwner) return -1;
        if (b.isOwner) return 1;
        return a.actor.name.localeCompare(b.actor.name);
    });
    return sharedWithDisplayList;
}
function isEqualActorList(listA: ActorInfo[], listB: ActorInfo[]): boolean {
    if (listA.length !== listB.length) return false;
    const sortedListA = [...listA].sort((a, b) => a.id.localeCompare(b.id));
    const sortedListB = [...listB].sort((a, b) => a.id.localeCompare(b.id));

    for (let i = 0; i < sortedListA.length; i++) {
        if (sortedListA[i].id !== sortedListB[i].id) {
            return false;
        }
    }
    return true;
}
